import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CartModificationPayload } from '@cart/const';
import { HTTP_CONTEXT_ENDPOINT } from '@core/const';
import {
  CustomOrder,
  CustomOrderHistoryList,
  OCC_ENDPOINTS,
  OfflineOrderLoadListParams,
  OnlineOrderLoadListParams,
  RepeatOfflineOrderParams,
  RepeatOrderParams,
} from '@order/const';
import { ConverterService, ORDER_NORMALIZER, Occ, OccEndpointsService, OrderHistoryList } from '@spartacus/core';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OccOrderService {
  constructor(
    protected http: HttpClient,
    protected occEndpoints: OccEndpointsService,
    protected converter: ConverterService,
  ) {}

  loadHistory({ orderType, pageSize, currentPage, sort }: OnlineOrderLoadListParams): Observable<OrderHistoryList> {
    const params: { [key: string]: string } = {
      orderType,
    };

    if (pageSize) {
      params['pageSize'] = pageSize.toString();
    }
    if (currentPage) {
      params['currentPage'] = currentPage.toString();
    }
    if (sort) {
      params['sort'] = sort.toString();
    }

    const url = this.occEndpoints.buildUrl(OCC_ENDPOINTS.orderHistory, {
      queryParams: params,
    });

    return this.http.get<Occ.OrderHistoryList>(url, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, OCC_ENDPOINTS.orderHistory),
    });
  }

  loadOfflineHistory({ pageSize, currentPage, sort }: OfflineOrderLoadListParams): Observable<CustomOrderHistoryList> {
    const params: { [key: string]: string } = {};

    if (pageSize) {
      params['pageSize'] = pageSize.toString();
    }
    if (currentPage) {
      params['currentPage'] = currentPage.toString();
    }
    if (sort) {
      params['sort'] = sort.toString();
    }

    const url = this.occEndpoints.buildUrl(OCC_ENDPOINTS.offlineOrdersHistory, {
      queryParams: params,
    });

    return this.http.get<CustomOrderHistoryList>(url, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, OCC_ENDPOINTS.offlineOrdersHistory),
    });
  }

  getOrderInvoicePayment(orderId: string): Observable<Blob> {
    const url = this.occEndpoints.buildUrl(OCC_ENDPOINTS.orderInvoicePayment, {
      urlParams: { orderId },
    });

    return this.http.get(url, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, OCC_ENDPOINTS.orderInvoicePayment),
      responseType: 'blob',
    });
  }

  repeatOrder({ orderCode, cartId }: RepeatOrderParams): Observable<CartModificationPayload> {
    const url = this.occEndpoints.buildUrl(OCC_ENDPOINTS.repeatOrder, {
      urlParams: { cartId },
      queryParams: { orderCode },
    });

    return this.http.post<CartModificationPayload>(url, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, OCC_ENDPOINTS.repeatOrder),
    });
  }

  repeatOfflineOrder({ offlineOrderCode, cartId }: RepeatOfflineOrderParams): Observable<CartModificationPayload> {
    const url = this.occEndpoints.buildUrl(OCC_ENDPOINTS.repeatOfflineOrder, {
      urlParams: { cartId },
      queryParams: { offlineOrderCode },
    });

    return this.http.post<CartModificationPayload>(url, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, OCC_ENDPOINTS.repeatOfflineOrder),
    });
  }

  loadOnlineOrderDetails(orderCode: string): Observable<CustomOrder> {
    const url = this.occEndpoints.buildUrl(OCC_ENDPOINTS.orderDetail, {
      urlParams: { orderId: orderCode },
      queryParams: { fields: 'FULL' },
    });

    return this.http
      .get<Occ.Order>(url, {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, OCC_ENDPOINTS.orderDetail),
      })
      .pipe(this.converter.pipeable(ORDER_NORMALIZER));
  }

  loadOfflineOrderDetails(orderCode: string): Observable<CustomOrder> {
    const url = this.occEndpoints.buildUrl(OCC_ENDPOINTS.offlineOrderDetails, {
      urlParams: { orderId: orderCode },
      queryParams: { fields: 'FULL' },
    });

    return this.http
      .get<Occ.Order>(url, {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, OCC_ENDPOINTS.offlineOrderDetails),
      })
      .pipe(this.converter.pipeable(ORDER_NORMALIZER));
  }
}
